import * as React from "react";
import {useEffect, useRef, useState} from "react";

import {createBookmarksModel, BookmarkFilter, BookmarksModel} from "../shift_shift/bookmarks/bookmarks_model";
import ListOfBookmarks from "../shift_shift/bookmarks/list_of_bookmarks";

import FilterInput from "../shift_shift/filter_input";
import KeyboardNavigation from "../shift_shift/keyboard_navigation";

export type BookmarkSelectorProps = {
    /**
     * Initial text to set in filter field and to filter by
     */
    filterText?: string;

    /**
     * Optional filter to narrow down which bookmarks are shown
     */
    bookmarkFilter?: BookmarkFilter;

    /**
     * Callback invoked when a bookmark is selected.
     * Receives the bookmark object found via maxkir.Bookmarks.find_bookmark_by_id.
     */
    onBookmarkSelected: (bookmark: maxkir_Bookmark) => void;

    updateHeightCallback(): void;
};

export const BookmarkSelector = (props: BookmarkSelectorProps) => {

    const [model] = useState<BookmarksModel>(() =>
        createBookmarksModel({bookmarkFilter: props.bookmarkFilter})
    );

    const [currentText, setCurrentText] = useState(props.filterText || "");
    const [lastModelUpdate, setLastModelUpdate] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const renderedItemsCount = useRef(0);

    useEffect(() => {
        props.updateHeightCallback();
    }, [lastModelUpdate]);

    const onBeforeItemOpen = (_saveState: boolean) => {
        maxkir.cmd && maxkir.cmd.close_all_popups();
    };

    const onKeyDown = (e) => {
        if (maxkir.is_text_input(e.target) && e.target.value) {
            // To not trigger when there is something typed in the text field
            return;
        }

        // Handle pressing 'N' when selecting a bookmark
        const num = parseInt(e.key);
        if (num >= 0 && num <= 9) {
            const selectedItem = model.findBookmarkByShortcut(num);
            if (selectedItem) {
                onBeforeItemOpen(true);
                props.onBookmarkSelected(selectedItem);
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }

        const keyboard = new KeyboardNavigation({
            getItemsCount: () => renderedItemsCount.current,
            getSelectedIndex: () => selectedIndex,
            setSelectedIndex: (idx: number) => setSelectedIndex(idx),
        });
        keyboard.onKeyDown(e);
    };

    const onFilterTextChanged = (text: string, _memorize: boolean) => {
        setCurrentText(text);
        model.onTextChanged(text, true);
    };

    useEffect(() => {
        const modelUpdated = () => {
            setLastModelUpdate(new Date());
            if (model.shouldResetSelection()) {
                setSelectedIndex(0);
            }
            setCurrentText(model.getFilterTerms().join(" "));
        };

        model.addDataAvailableCallback(modelUpdated);
        model.onTextChanged(currentText, false);

        return () => {
            model.removeDataAvailableCallback(modelUpdated);
            model.dispose();
        };
    }, []);

    return <div className="searchEverywhere searchEverywhere__bookmarkSelector" tabIndex={-1}
                onKeyDown={onKeyDown}>

        <p className={'intro inMenu'}
           dangerouslySetInnerHTML={{__html: I18N('js_move_to_bookmark_title')}}></p>

        <FilterInput filterText={currentText} onTextChanged={onFilterTextChanged} setFocus={0}
                     placeholder={I18N('js_move_to_bookmark_filter_placeholder')}
                     isChanged={currentText !== (props.filterText || "")}
                     inProgress={model.isLoading()}
        />
        <ListOfBookmarks
            model={model}
            onBeforeItemOpen={onBeforeItemOpen}
            onBookmarkSelected={props.onBookmarkSelected}
            selectedIndex={selectedIndex}
            renderedItemsCountCallback={(x) => {
                renderedItemsCount.current = x;
            }}
            isActiveTab={true}
        />
    </div>;
};
